import { useCallback, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '@/shared/lib/auth';
import { PageHeader } from '@/shared/components/page-header';
import { DataTable, type DataTableFetchParams } from '@/shared/components/data-table';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { formatDate, formatTime } from '@/shared/lib/format';
import {
  fetchDispatchRunResults,
  type DispatchRunResult,
  type DispatchRunResults,
} from '../api/use-dispatch-runs';

/** typeAccess gravado no envio → nome do dispositivo. */
function deviceLabel(typeAccess: string | undefined, channel: string | undefined): string {
  if (!typeAccess) return channel === 'whatsapp' ? 'WhatsApp' : channel === 'email' ? 'E-mail' : '—';
  if (typeAccess === 'APP-ios') return 'App iOS';
  if (typeAccess === 'APP-android') return 'App Android';
  if (typeAccess.startsWith('APP')) return 'App';
  if (typeAccess === 'iOS') return 'Apple Wallet';
  if (typeAccess === 'gwallet') return 'Google Wallet';
  return 'Wallet Android';
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'danger' }) {
  return (
    <Card className="p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${tone === 'danger' && value > 0 ? 'text-destructive' : ''}`}>
        {value.toLocaleString('pt-BR')}
      </p>
    </Card>
  );
}

/** Pra quem deu certo e pra quem falhou numa execução — GET /dispatch_runs/:id/results. */
export function DispatchRunDetailPage() {
  const { runId = '' } = useParams<{ runId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('all');
  const [summary, setSummary] = useState<DispatchRunResults['run'] | null>(null);

  const fetchData = useCallback(
    async ({ pageIndex, pageSize, search }: DataTableFetchParams) => {
      const res = await fetchDispatchRunResults(runId, {
        status: status === 'all' ? undefined : status,
        q: search || undefined,
        page: pageIndex,
        pageSize,
      });
      setSummary(res.run);
      return { rows: res.rows ?? [], total: res.total ?? 0 };
    },
    [runId, status],
  );

  const channel = summary?.tipo_disparo;
  const columns = useMemo<ColumnDef<DispatchRunResult, unknown>[]>(
    () => [
      {
        id: 'pessoa',
        header: 'Pessoa',
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate">{row.original.userName || '—'}</p>
            {row.original.serialNumber ? (
              <p className="truncate text-xs text-muted-foreground">Serial {row.original.serialNumber}</p>
            ) : null}
          </div>
        ),
      },
      {
        id: 'dispositivo',
        header: 'Dispositivo',
        cell: ({ row }) => deviceLabel(row.original.typeAccess, channel),
      },
      {
        id: 'status',
        header: 'Resultado',
        cell: ({ row }) =>
          row.original.status === 'success' ? (
            <Badge variant="success">Entregue</Badge>
          ) : (
            <Badge variant="destructive">Falhou</Badge>
          ),
      },
      {
        id: 'motivo',
        header: 'Motivo da falha',
        cell: ({ row }) => {
          if (row.original.status !== 'failed') return <span className="text-muted-foreground">—</span>;
          // Push gravado antes desta versão não tinha motivo: o campo guardava a própria mensagem.
          if (channel === 'push' && !row.original.typeAccess) {
            return <span className="text-muted-foreground">Não registrado (disparo antigo)</span>;
          }
          return <span className="text-sm">{row.original.return_message || '—'}</span>;
        },
      },
      {
        id: 'quando',
        header: 'Quando',
        cell: ({ row }) =>
          row.original.date ? (
            <span className="whitespace-nowrap text-xs text-muted-foreground">
              {formatDate(row.original.date)} {formatTime(row.original.date)}
            </span>
          ) : (
            '—'
          ),
      },
    ],
    [channel],
  );

  if (!user || user.role !== 'Admin') {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <PageHeader
        title="Detalhe do disparo"
        description={
          summary?.created_at
            ? `${formatDate(summary.created_at)} ${formatTime(summary.created_at)} · ${summary.tipo_disparo ?? ''}`
            : undefined
        }
        actions={
          <Button variant="secondary" onClick={() => navigate('/observabilidade?aba=disparos')}>
            <ArrowLeft className="size-4" /> Voltar
          </Button>
        }
      />

      {summary ? (
        <>
          <div className="mb-2 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat label="Público (pessoas)" value={summary.total ?? 0} />
            <Stat label="Tentativas (dispositivos)" value={summary.sent} />
            <Stat label="Entregues" value={summary.success} />
            <Stat label="Falhas" value={summary.failed} tone="danger" />
          </div>
          <p className="mb-4 text-xs text-muted-foreground">
            Tentativas = Entregues + Falhas. Uma pessoa com app e cartão na carteira conta como 2 tentativas, por isso
            Tentativas pode passar do Público.
          </p>
        </>
      ) : null}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full gap-1.5 pr-3 sm:w-auto sm:min-w-[11rem]">
            <SelectValue placeholder="Resultado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os resultados</SelectItem>
            <SelectItem value="success">Só entregues</SelectItem>
            <SelectItem value="failed">Só falhas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        fetchData={fetchData}
        searchPlaceholder="Buscar por nome ou serial…"
        emptyTitle="Nenhum envio com esse filtro"
        resetKey={status}
      />
    </>
  );
}
